"use client";
import { Search, X } from "lucide-react";
import {
  Activity,
  ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import useLocalizer from "@/hooks/useLocalizer";
import { useNavigation } from "@/components/shared/navigationProvider";
import { useRouter } from "@/i18n/routing";


interface NavbarSearchInputProps extends ComponentProps<"input"> {
  containerClassName?: string;
  onSelectResult?: (href: string) => void;
}

const NavbarSearchInput: React.FC<NavbarSearchInputProps> = ({
  className,
  containerClassName,
  onSelectResult,
  ...props
}) => {
  const { isRtl, t } = useLocalizer();
  const inputRef = useRef<HTMLInputElement>(null);
  const [text, setText] = useState<string>("");
  const { navigations, services, projects } = useNavigation();

  useEffect(() => {
    function handleInput() {
      setText(inputRef.current?.value || "");
    }

    if (inputRef.current) {
      inputRef.current.addEventListener("input", handleInput);
    }

    return () => {
      if (inputRef.current) {
        inputRef.current.removeEventListener("input", handleInput);
      }
    };
  }, [inputRef.current]);

  const handleClearText = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.value = "";
      setText("");
      inputRef.current.focus();
    }
  }, [inputRef.current]);

  const filteredResults = useMemo(() => {
    if (!text.trim()) return [];

    const query = text.toLowerCase().trim();
    const results: Array<{ title: string; href: string }> = [];

    // Search Pages
    navigations.forEach((page) => {
      const pageTitleAr = page.arTitle.toLowerCase();
      const pageTitleEn = page.enTitle.toLowerCase();
      if (pageTitleAr.includes(query) || pageTitleEn.includes(query)) {
        results.push({
          title: isRtl ? page.arTitle : page.enTitle,
          href: page.href,
        });
      }

      page.subPages?.forEach((sub) => {
        const subTitleAr = sub.arTitle.toLowerCase();
        const subTitleEn = sub.enTitle.toLowerCase();
        if (subTitleAr.includes(query) || subTitleEn.includes(query)) {
          results.push({
            title: `${isRtl ? page.arTitle : page.enTitle} > ${isRtl ? sub.arTitle : sub.enTitle}`,
            href: sub.href || "#",
          });
        }
      });
    });

    // Search Services
    services.forEach((service) => {
      const titleAr = service.arTitle.toLowerCase();
      const titleEn = service.enTitle.toLowerCase();
      if (titleAr.includes(query) || titleEn.includes(query)) {
        results.push({
          title: `${isRtl ? "خدمة" : "Service"}: ${isRtl ? service.arTitle : service.enTitle}`,
          href: `/services/${service.id}`,
        });
      }
    });

    // Search Projects
    projects.forEach((project) => {
      const titleAr = project.arTitle.toLowerCase();
      const titleEn = project.enTitle.toLowerCase();
      if (titleAr.includes(query) || titleEn.includes(query)) {
        results.push({
          title: `${isRtl ? "عمل" : "Project"}: ${isRtl ? project.arTitle : project.enTitle}`,
          href: `/projects/${project.id}`,
        });
      }
    });

    return results;
  }, [text, navigations, services, projects, isRtl]);

  return (
    <div className="flex flex-col gap-2 w-full max-w-2xl mx-auto">
      <div
        className={cn(
          "flex flex-row items-center justify-center bg-white h-20 rounded-sm px-4 w-full border border-border",
          containerClassName,
        )}
      >
        <Search size={24} className="text-secondary-foreground" />
        <Input
          tabIndex={1}
          ref={inputRef}
          className="border-none! shadow-none lg:text-lg text-sm ring-0! outline-0! focus:ring-0! bg-transparent!"
          {...props}
        />
        <Activity mode={text ? "visible" : "hidden"}>
          <Button
            variant="secondary"
            type="button"
            className="h-10 w-10 border border-secondary-foreground hover:[&>svg]:scale-120"
            onClick={handleClearText}
            aria-label={t("buttons.clear")}
          >
            <X size={24} />
          </Button>
        </Activity>
      </div>

      <Activity mode={filteredResults.length > 0 ? "visible" : "hidden"}>
        <div className="bg-white border border-border rounded-sm shadow-xl overflow-hidden">
          <div className="max-h-60 overflow-y-auto">
            <div className="flex flex-col">
              {filteredResults.map((result, index) => (
                <button
                  key={index}
                  onClick={() => onSelectResult?.(result.href)}
                  className="w-full text-start px-4 py-3 hover:bg-secondary/10 transition-colors border-b last:border-0 border-border"
                >
                  <p className="font-medium text-secondary-foreground">
                    {result.title}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>
      </Activity>
    </div>
  );
};

export const NavbarSearchButton: React.FC = () => {
  const { t } = useLocalizer();
  const router = useRouter();
  const [open, setOpen] = useState(false);

  const handleSelect = useCallback(
    (href: string) => {
      setOpen(false);
      router.push(href);
    },
    [router],
  );

  return (
    <Dialog modal open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="inline-flex items-center" aria-label="Search">
          <Search
            size={32}
            className="text-secondary-foreground hover:text-primary"
          />
        </button>
      </DialogTrigger>
      <DialogContent
        showCloseButton={false}
        className="bg-transparent! border-none! min-w-full top-[5%] shadow-none z-9999"
      >
        <DialogHeader>
          <DialogTitle />
        </DialogHeader>
        <NavbarSearchInput
          onSelectResult={handleSelect}
          placeholder={t("placeholders.searchAnyThing")}
        />
      </DialogContent>
    </Dialog>
  );
};

export default NavbarSearchButton;
